import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dashboardApi } from '../api';
import { DashboardTopicValue } from '../types';

export type MqttValuesTransport = 'poll' | 'idle';

const POLL_MS = 3000;

/**
 * Live MQTT topic values via HTTP polling.
 * WebSocket push is deferred (server already exposes /api/mqtt/values for later).
 */
export function useLiveMqttValues(topics?: string[] | null): {
  values: Record<string, DashboardTopicValue | null>;
  mqttConfigured: boolean;
  transport: MqttValuesTransport;
  refresh: () => Promise<void>;
} {
  const [values, setValues] = useState<Record<string, DashboardTopicValue | null>>({});
  const [mqttConfigured, setMqttConfigured] = useState(true);
  const [transport, setTransport] = useState<MqttValuesTransport>('idle');

  const topicsKey = useMemo(
    () => (topics && topics.length ? topics.slice().sort().join('\n') : ''),
    [topics],
  );
  const topicsRef = useRef<string[]>(topics || []);
  topicsRef.current = topics || [];

  const pollOnce = useCallback(async () => {
    const list = topicsRef.current;
    const res = await dashboardApi.getValues(list.length ? list : undefined);
    setValues(res.values || {});
    setMqttConfigured(res.mqttConfigured !== false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTransport('poll');
    const tick = () => {
      pollOnce().catch(() => undefined);
    };
    tick();
    const pollTimer = setInterval(() => {
      if (!cancelled) tick();
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(pollTimer);
    };
  }, [topicsKey, pollOnce]);

  const refresh = useCallback(async () => {
    await pollOnce();
  }, [pollOnce]);

  return { values, mqttConfigured, transport, refresh };
}
